import {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { RegistrationInput } from "@/components/valuation/RegistrationInput";
import { PostAnAdDetailsForm } from "./PostAnAdDetailsForm";
import { Button } from "@/components/ui/button";
import VehicleImageUploadSection, {
  type VehicleImageUploadSectionHandle,
} from "./VehicleImageUploadSection";
import { PriceSection } from "./PriceSection";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import type { VehicleDetails } from "@/types/vehicleDetails";
import { VehicleDetailsForm } from "../valuation/VehicleDetailsForm";
import { useTest } from "@/hooks/useTest";
import { useListing } from "@/hooks/useListing";
import {
  CreateTestRequest,
  UpdateTestRequest,
  VehicleTest,
  UpdateTestResponse,
  VehicleTestImages,
  VehicleTestImageFiles,
  VehicleTestData,
  UpdateVehicleTestImagesRequest,
  ExteriorImage,
} from "@/types/vehicleTest";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { authLabels, listingPostLabels } from "@/i18n";
import { carTestLabels } from "@/i18n/labels";
import { getLanguage } from "@/utils/languageUtils";
import { useMemo } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, X, Loader2 } from "lucide-react";
import { useVehicleLookup } from "@/hooks/useVehicleLookup";
import { useIsMobile } from "@/hooks/use-mobile";
import type { VehicleEstimationResponse } from "@/types/valuation";
import { sanitizeErrorMessage } from "@/utils/errorUtils";
import { convertAutoUncleToVehicleDetails, toVehicleDetailsPatch } from "@/selectors/autoUncleSelectors";
import { storedVehicleDetailsToForm } from "@/utils/storedVehicleDetailsToForm";
import { isBildetaljerComplete, isVehicleInfoComplete } from "@/utils/vehicleInfoCompletion";
import { SelectSellerDialog } from "./SelectSellerDialog";
import { UserSummary } from "@/types/userDetails";
import { useUserManagement } from "@/hooks/useUserManagement";
import { AutoUncleVerificationDialog } from "./AutoUncleVerificationDialog";
import { useSettings } from "@/hooks/useSettings";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DeliverySection } from "@/components/settings/profile/DeliverySection";
import { AdminUserDeliverySection } from "@/admin/settings/user/AdminUserDeliverySection";
import { UserManagementService } from "@/services/user/userManagementService";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { nativeNestedSheetDialogProps } from "@/lib/native/nativeNestedSheetProps";
import { cn } from "@/lib/utils";
import { toPriceSectionVehicleInfo } from "@/utils/priceSectionVehicleInfo";

export type CarPostAnAdWidgetHandle = {
  saveImageStepIfDirty: () => Promise<void>;
};

export interface CarPostAnAdWidgetProps {
  initialData?: {
    id?: string; // Add test ID for image updates
    regNumber?: string;
    vehicleDetails?: Partial<VehicleDetails>;
    vehicleTestData?: VehicleTestData;
    images?: VehicleTestImages;
    price?: string;
    comments?: string;
    vatCar?: boolean;
    initialStep?: number;
    status: string;
    customerId?: string;
    checkIfAllegedAttourneyInFact?: boolean;
    purchasePrice?: number;
  };
  isEditMode?: boolean;
  isCompletionMode?: boolean;
  onTestSaved?: (updatedTest: UpdateTestResponse) => void;
  onOpenChange?: (open: boolean) => void;
  title?: string;
}

export const CarPostAnAdWidget = forwardRef<
  CarPostAnAdWidgetHandle,
  CarPostAnAdWidgetProps
>(function CarPostAnAdWidget(
  {
    initialData,
    isEditMode = false,
    isCompletionMode = false,
    onTestSaved,
    onOpenChange,
    title,
  }: CarPostAnAdWidgetProps,
  ref,
) {
  // Calculate which step user actually reached based on data
  // If user has completed Info step (has make) and has a test ID (meaning they saved while on Test step),
  // they've reached Test step even if they haven't answered questions yet
  const calculateActualStepReached = useCallback(() => {
    let maxDataStep = 0;
    if (initialData?.regNumber) maxDataStep = Math.max(maxDataStep, 0);
    if (initialData?.vehicleDetails?.make)
      maxDataStep = Math.max(maxDataStep, 1);

    // If user has test data with speed, they've definitely reached Test step
    if (initialData?.vehicleTestData?.speed) {
      maxDataStep = Math.max(maxDataStep, 2);
    }
    // If user has completed Info step (has make) and has a test ID (saved draft exists),
    // and we're in completion mode (opening existing test), they've likely reached Test step
    // even without test data answers - this handles the case where user navigated to Test step
    // but saved before answering questions
    else if (
      isCompletionMode &&
      initialData?.id &&
      initialData?.vehicleDetails?.make &&
      !initialData?.vehicleTestData?.speed
    ) {
      // User has Info completed, test exists, but no test data - they likely navigated to Test step
      maxDataStep = Math.max(maxDataStep, 2);
    }

    if (initialData?.images) maxDataStep = Math.max(maxDataStep, 3);
    if (initialData?.price) maxDataStep = Math.max(maxDataStep, 4);
    return maxDataStep;
  }, [
    initialData?.regNumber,
    initialData?.vehicleDetails?.make,
    initialData?.vehicleTestData?.speed,
    initialData?.id,
    initialData?.images,
    initialData?.price,
    isCompletionMode,
  ]);

  // When in completion mode, use the actual step reached based on data
  // When editing, use initialStep from initialData if provided, otherwise step 1
  // Otherwise, use initialStep from initialData or 0
  const getInitialStep = () => {
    if (isCompletionMode) {
      // In completion mode, start at the step where user actually left off
      const actualStep = calculateActualStepReached();
      // Ensure we're at least at step 1 (Info) since Reg nr is hidden in completion mode
      return Math.max(actualStep, 1);
    }
    if (isEditMode) {
      // In edit mode, use initialStep if provided, otherwise step 1 (Info)
      return initialData?.initialStep ?? 1;
    }
    // Normal mode: use initialStep from initialData or 0
    return initialData?.initialStep || 0;
  };

  const initialStep = getInitialStep();
  const [step, setStep] = useState(initialStep);

  const navigate = useNavigate();
  const { language } = useLanguage();
  const labels = useMemo(() => authLabels(getLanguage()), []);
  const lp = useMemo(() => listingPostLabels(language), [language]);
  const sd = lp.submitDelivery ?? ({} as Record<string, string>);
  const ct = useMemo(() => carTestLabels(language), [language]);
  const STEP_LABELS = useMemo(
    () => [
      lp.steps.search,
      lp.steps.vehicleInfo,
      lp.steps.test,
      lp.steps.images,
      lp.steps.price,
    ],
    [lp.steps],
  );
  const STEP_LABELS_MOBILE = useMemo(
    () => [
      lp.steps.searchShort,
      lp.steps.infoShort,
      lp.steps.testShort,
      lp.steps.images,
      lp.steps.price,
    ],
    [lp.steps],
  );

  // Calculate max step reached based on initial data
  const calculateMaxStepReached = () => {
    // When editing or completing a draft, minimum step is 1 (Info) since Reg nr is hidden
    const minStep =
      isEditMode || isCompletionMode
        ? Math.max(initialData?.initialStep || 1, 1)
        : initialData?.initialStep || 0;

    // Check which steps have data
    let maxDataStep = 0;
    if (initialData?.regNumber) maxDataStep = Math.max(maxDataStep, 0);
    if (initialData?.vehicleDetails?.make)
      maxDataStep = Math.max(maxDataStep, 1);

    // If user has test data with speed, they've reached Test step
    if (initialData?.vehicleTestData?.speed) {
      maxDataStep = Math.max(maxDataStep, 2);
    }

    if (initialData?.images) maxDataStep = Math.max(maxDataStep, 3);
    if (initialData?.price) maxDataStep = Math.max(maxDataStep, 4);

    // Return the higher of minStep and maxDataStep
    return Math.max(minStep, maxDataStep);
  };

  const [maxStepReached, setMaxStepReached] = useState(
    calculateMaxStepReached(),
  );
  // Initialize regNumber - check both top-level regNumber and vehicleDetails.regNumber
  const [regNumber, setRegNumber] = useState(
    initialData?.regNumber || initialData?.vehicleDetails?.regNumber || "",
  );
  const [vehicleDetails, setVehicleDetails] = useState<Partial<VehicleDetails>>(
    () => {
      // Use lazy initializer to ensure we get initialData if it's available
      const baseDetails = storedVehicleDetailsToForm(
        initialData?.vehicleDetails || {},
      );
      // Ensure regNumber is included in vehicleDetails from initialData if available
      const detailsWithRegNumber = {
        ...baseDetails,
        regNumber: initialData?.regNumber || baseDetails.regNumber || "",
      };

      // If extraTireSet is explicitly set in initialData (including false), use it as-is
      // When editing a test, never infer extraTireSet from extraRimType/extraTireType
      // Only infer it when creating a new test (not in edit mode) and extraTireSet is truly undefined
      // This ensures that false values from database are preserved, not overwritten
      if (detailsWithRegNumber.extraTireSet !== undefined) {
        // extraTireSet is already set (true or false), use it as-is
        return detailsWithRegNumber.equipment
          ? detailsWithRegNumber
          : { ...detailsWithRegNumber, equipment: [] };
      } else if (
        !isEditMode && // Only infer when creating new test, not editing
        detailsWithRegNumber.extraRimType &&
        detailsWithRegNumber.extraTireType
      ) {
        // extraTireSet not set but both extraRimType and extraTireType exist, infer it (only for new tests)
        return {
          ...detailsWithRegNumber,
          extraTireSet: true,
        };
      }
      // If extraTireSet is undefined, default to false (especially important when editing)
      return detailsWithRegNumber.equipment
        ? { ...detailsWithRegNumber, extraTireSet: false }
        : { ...detailsWithRegNumber, equipment: [], extraTireSet: false };
    },
  );
  // Track if user has manually edited vehicleDetails to prevent overwriting
  const userEditedVehicleDetailsRef = useRef(false);
  // Track previous make/model/year to detect changes that should reset steps
  const prevBasicVehicleInfoRef = useRef({
    make: initialData?.vehicleDetails?.make,
    model: initialData?.vehicleDetails?.model,
    year: initialData?.vehicleDetails?.year,
  });
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [isAutoLoaded, setIsAutoLoaded] = useState(
    !!initialData?.vehicleDetails,
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [hasActiveListing, setHasActiveListing] = useState(false);
  const [notOwner, setNotOwner] = useState(false);
  const [checkingActiveListing, setCheckingActiveListing] = useState(false);
  const [pendingRegNumber, setPendingRegNumber] = useState<string | null>(null);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  // Vehicle lookup hook
  const { getVehicleInformationAsync, isLoading: isLookingUpVehicle } =
    useVehicleLookup();
  const [vehicleTestData, setVehicleTestData] = useState<VehicleTestData>(
    initialData?.vehicleTestData || {},
  );
  const [checkIfAllegedAttourneyInFact, setCheckIfAllegedAttourneyInFact] =
    useState();
  const [images, setImages] = useState<VehicleTestImages>(
    initialData?.images || {},
  );
  const [imageFiles, setImageFiles] = useState<VehicleTestImageFiles>({});
  const [price, setPrice] = useState(initialData?.price || "");
  const [comments, setComments] = useState(initialData?.comments || "");
  const [vatCar, setVatCar] = useState(initialData?.vatCar || false);
  const [purchasePrice, setPurchasePrice] = useState<number | undefined>(
    initialData?.purchasePrice,
  );
  const [successMessage, setSuccessMessage] = useState("");
  const [status, setStatus] = useState(initialData?.status || "ongoing");
  const [hasNonImageChanges, setHasNonImageChanges] = useState(false);
  const [showSelectSellerDialog, setShowSelectSellerDialog] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<UserSummary | null>(
    null,
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [createdTestId, setCreatedTestId] = useState<string | undefined>(
    initialData?.id,
  );
  // Ref to always have the latest testId, even if React state hasn't updated yet
  // This prevents creating duplicate tests when submit happens right after test creation
  const createdTestIdRef = useRef<string | undefined>(initialData?.id);
  const imageUploadRef = useRef<VehicleImageUploadSectionHandle>(null);
  const [imageStepDirty, setImageStepDirty] = useState(false);
  const [isCreatingTestForImages, setIsCreatingTestForImages] = useState(false);

  useEffect(() => {
    if (step !== 3) {
      setImageStepDirty(false);
    }
  }, [step]);

  const { deliveryAddresses, refetchDeliveryAddresses } = useSettings();
  const [userDeliveryPromptOpen, setUserDeliveryPromptOpen] = useState(false);
  const [userDeliveryFormOpen, setUserDeliveryFormOpen] = useState(false);
  const [adminSellerDeliveryPromptOpen, setAdminSellerDeliveryPromptOpen] =
    useState(false);
  const [adminSellerDeliveryUserId, setAdminSellerDeliveryUserId] = useState<
    string | null
  >(null);
  const [adminSellerDeliveryFormOpen, setAdminSellerDeliveryFormOpen] =
    useState(false);
  const [isSendingSellerReminder, setIsSendingSellerReminder] = useState(false);
  const sellerDeliveryGatePassedRef = useRef(false);
  /** Non-admin: user chose "Jag lägger till senare" — submit without delivery address. */
  const userDeliverySkipRef = useRef(false);
  const pendingAdminSubmitSellerIdRef = useRef<string | undefined>(undefined);
  const [reviewSubmitBusy, setReviewSubmitBusy] = useState(false);
  const userDeliveryPromptSheetProps =
    nativeNestedSheetDialogProps(userDeliveryPromptOpen);
  const userDeliveryFormSheetProps = nativeNestedSheetDialogProps(
    userDeliveryFormOpen,
    { expandable: true, form: true },
  );
  const adminSellerDeliveryPromptSheetProps = nativeNestedSheetDialogProps(
    adminSellerDeliveryPromptOpen,
  );
  const adminSellerDeliveryFormSheetProps = nativeNestedSheetDialogProps(
    adminSellerDeliveryFormOpen,
    { expandable: true, form: true },
  );
  const { getUserDeliveryAddresses, fetchUserDeliveryAddressesAsync } =
    useUserManagement(
      undefined,
      1,
      1,
      undefined,
      undefined,
      undefined,
      undefined,
      {
        enabled: false,
      },
    );

  const {
    data: adminSellerDeliveryAddresses = [],
    isFetching: loadingAdminSellerAddresses,
    refetch: refetchAdminSellerDeliveryAddresses,
  } = getUserDeliveryAddresses(adminSellerDeliveryUserId ?? "", {
    enabled: adminSellerDeliveryFormOpen,
  });

  useImperativeHandle(ref, () => ({
    async saveImageStepIfDirty() {
      if (!imageUploadRef.current) return;
      setIsSavingChanges(true);
      try {
        await imageUploadRef.current.savePendingImages?.();
      } finally {
        setIsSavingChanges(false);
      }
    },
  }));

  // AutoUncle verification popup state
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationData, setVerificationData] = useState<{
    transmission?: string;
    engineVolume?: string;
    fuelType?: string;
    bodyType?: string;
    doors?: number;
    horsePower?: string;
    electricDriveRange?: number;
    /** Drives the MC exemption for the engine-volume check. */
    vehicleType?: "car" | "mc";
  } | null>(null);

  // Stable callback for image changes
  const handleImagesChange = useCallback((newImages: VehicleTestImages) => {
    setImages(newImages);
  }, []);

  // Callback for image file changes
  const handleImageFilesChange = useCallback((files: VehicleTestImageFiles) => {
    setImageFiles(files);
  }, []);

  // Use the test hook
  const {
    createTest,
    updateTest,
    submitTest,
    updateTestError,
    isUpdatingTest,
    isCreatingTest,
    isSubmittingTest,
    createTestError,
    submitTestError,
    updateVehicleTestImages,
    invalidateTestImages,
    getVehicleBasicInformationByLpn,
    addCarTestImage,
  } = useTest({ disableAdminQuery: true, disableUserQuery: true });

  // Handle saving image changes separately
  const handleSaveImageChanges = useCallback(
    async (data: UpdateVehicleTestImagesRequest) => {
      if (!data?.testId) return;

      try {
        await updateVehicleTestImages(data);
        toast.success(ct.messages.toastImageChangesSaved);
      } catch (error) {
        toast.error(ct.messages.toastImageChangesError);
        throw error; // Re-throw to let component handle loading state
      }
    },
    [updateVehicleTestImages, ct],
  );

  // Get user info for navigation
  const { user } = useAuth();
  const isMobile = useIsMobile();

  // Use listing hook for checking active listings
  const { getListingsWithFilters } = useListing();

  // Determine if input is VIN (17 characters) or registration number
  const isVin = pendingRegNumber ? pendingRegNumber.length === 17 : false;

  // Use the hook to check for active listings
  const activeListingsQuery = getListingsWithFilters({
    ...(isVin ? { vin: pendingRegNumber } : { regNumber: pendingRegNumber }),
    status: "active",
  });

  // Function to proceed with normal flow after checking for active listings
  const proceedWithNormalFlow = useCallback(
    async (reg: string) => {
      try {
        setLoading(true);

        // Lookup vehicle information
        const result = await getVehicleInformationAsync({
          lpn: reg,
        });

        // Check if result is empty or undefined
        if (!result || !result.rawResponse || !result.rawResponse.car) {
          throw new Error("Vehicle information not found");
        }

        const rawCar = result.rawResponse.car;

        // Check if critical fields are missing - but be more lenient
        // Only return early if ALL THREE critical fields are missing
        const hasBrand = rawCar.brand && rawCar.brand !== "";
        const hasModel = rawCar.car_model && rawCar.car_model !== "";
        const hasYear =
          rawCar.year &&
          rawCar.year !== 0 &&
          rawCar.year !== null &&
          rawCar.year !== undefined;

        const missingAllCriticalFields = !hasBrand && !hasModel && !hasYear;

        if (missingAllCriticalFields) {
          // DEBUG: Uncomment for debugging missing data issues
          // console.warn("[CarPostAnAd] ⚠️ Missing all critical fields, using minimal data");
          setVehicleDetails({
            regNumber: rawCar.lpn,
            vin: rawCar.vin,
            engineVolume: rawCar.engineVolume,
          });
          setIsAutoLoaded(true);
          setLoading(false);
          setShowSuccess(true);
          setCheckingActiveListing(false);
          setPendingRegNumber(null);

          setTimeout(() => {
            setShowSuccess(false);
            setStep(1);
          }, 1200);
          return;
        }

        // The backend envelope is already in the shape the selector expects.
        const carInfoForSelector = {
          ...rawCar,
          km: rawCar.km || 0,
          variant: rawCar.variant || "",
          doors: rawCar.doors,
        };

        const convertedDetails = convertAutoUncleToVehicleDetails(
          carInfoForSelector as any,
        );

        // IMPORTANT: Elbilar är alltid automatiska, så sätt "Automat" direkt för elbilar
        const isElectric =
          rawCar.fuel === "El" ||
          rawCar.fuel === "Elbil" ||
          rawCar.fuel === "ELECTRIC";
        if (isElectric) {
          convertedDetails.transmission = "Automat";
        }

        // Build complete vehicleDetails object - map ALL fields explicitly
        // This ensures 100% mapping from AutoUncle response to our internal format
        // IMPORTANT: Use non-falsy check with explicit fallbacks to handle empty strings and 0 values correctly
        // Build the VehicleDetails patch via the shared mapper so that every field the
        // lookup returns reaches the form. This used to be a hand-enumerated object
        // literal, which silently dropped any newly added field.
        const vehicleDetails: Partial<VehicleDetails> = {
          ...toVehicleDetailsPatch(carInfoForSelector as any),
          // EVs are always automatic regardless of what the provider reports.
          ...(convertedDetails.transmission
            ? { transmission: convertedDetails.transmission }
            : {}),
        };

        // DEBUG: Uncomment for debugging data mapping issues
        // console.log("[CarPostAnAd] 📝 Final vehicleDetails before setState:", {
        //   make: vehicleDetails.make,
        //   model: vehicleDetails.model,
        //   variant: vehicleDetails.variant,
        //   year: vehicleDetails.year,
        //   fuelType: vehicleDetails.fuelType,
        //   bodyType: vehicleDetails.bodyType,
        //   transmission: vehicleDetails.transmission,
        //   color: vehicleDetails.color,
        //   engineVolume: vehicleDetails.engineVolume,
        //   horsepower: vehicleDetails.horsepower,
        //   doors: vehicleDetails.doors,
        //   mileage: vehicleDetails.mileage,
        //   equipment: vehicleDetails.equipment,
        //   vin: vehicleDetails.vin,
        //   regNumber: vehicleDetails.regNumber,
        //   isElectric,
        // });

        // Vehicle found - populate form with data
        setVehicleDetails(vehicleDetails);
        // Update ref with loaded data
        prevBasicVehicleInfoRef.current = {
          make: vehicleDetails.make,
          model: vehicleDetails.model,
          year: vehicleDetails.year,
        };
        setIsAutoLoaded(true);
        setLoading(false);
        setShowSuccess(true);
        setCheckingActiveListing(false);
        setPendingRegNumber(null);

        setTimeout(() => {
          setShowSuccess(false);
          setStep(1);
        }, 1200);
      } catch (error) {
        const apiError = error as {
          status?: number | string;
          errorCode?: string | null;
        };
        if (
          apiError?.errorCode === "CARINFO_RATE_LIMITED" ||
          Number(apiError?.status) === 429
        ) {
          setRateLimited(true);
        } else {
          setNotFound(true);
        }
        setLoading(false);
        setCheckingActiveListing(false);
        setPendingRegNumber(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // mutateAsync is stable from React Query
  );

  // Effect to handle active listing check results
  useEffect(() => {
    if (!checkingActiveListing || !pendingRegNumber) return;

    if (activeListingsQuery.isLoading) {
      // Still loading, wait
      return;
    }

    if (activeListingsQuery.error) {
      // Continue with normal flow if there's an error
      proceedWithNormalFlow(pendingRegNumber);
    } else if (
      activeListingsQuery.data?.listings &&
      activeListingsQuery.data.listings.length > 0
    ) {
      setHasActiveListing(true);
      setLoading(false);
      setCheckingActiveListing(false);
      setPendingRegNumber(null);
      return;
    } else {
      // No active listing found, proceed with normal flow
      proceedWithNormalFlow(pendingRegNumber);
    }
  }, [
    activeListingsQuery.isLoading,
    activeListingsQuery.data,
    activeListingsQuery.error,
    checkingActiveListing,
    pendingRegNumber,
    proceedWithNormalFlow,
  ]);

  // Safety measure: Warn user when trying to navigate away with unsaved work
  useEffect(() => {
    const shouldWarn =
      isEditMode || isCompletionMode
        ? hasNonImageChanges || imageStepDirty
        : step >= 1 || maxStepReached >= 1 || imageStepDirty;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldWarn) {
        e.preventDefault();
        return "Alla ändringar kommer att gå förlorade. Är du säker på att du vill lämna?";
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (shouldWarn) {
        const confirmed = window.confirm(
          "Alla ändringar kommer att gå förlorade. Är du säker på att du vill lämna?",
        );
        if (!confirmed) {
          e.preventDefault();
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    if (shouldWarn) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("popstate", handlePopState);
      window.history.pushState(null, "", window.location.href);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [
    step,
    maxStepReached,
    isEditMode,
    isCompletionMode,
    hasNonImageChanges,
    imageStepDirty,
  ]);

  // Emit progress events for popup dialogs
  useEffect(() => {
    const event = new CustomEvent("carPostAdProgress", {
      detail: {
        // Edit/completion: only warn when something actually changed.
        // Image dirtiness comes from imageStepDirty (section tracking), not parent images JSON.
        hasProgress: isEditMode || isCompletionMode
          ? hasNonImageChanges || imageStepDirty
          : step >= 1 || maxStepReached >= 1 || imageStepDirty,
        imageStepDirty,
      },
    });
    window.dispatchEvent(event);
  }, [
    step,
    maxStepReached,
    imageStepDirty,
    isEditMode,
    isCompletionMode,
    hasNonImageChanges,
  ]);

  // Normal + edit: only sync step when test id / declared initialStep changes — never when images/price
  // on initialData update mid-session (avoids jumping back to Bild after save/submit).
  useEffect(() => {
    if (isCompletionMode) return;
    if (!initialData && !isEditMode) return;

    const adjustedStep = isEditMode
      ? Math.max(initialData?.initialStep ?? 1, 1)
      : (initialData?.initialStep ?? 0);

    setStep(adjustedStep);
    setMaxStepReached((prev) => Math.max(prev, adjustedStep));
  }, [initialData?.id, initialData?.initialStep, isEditMode, isCompletionMode]);

  // Completion mode: derive step from saved data (may legitimately change when initialData deep data updates).
  useEffect(() => {
    if (!isCompletionMode || !initialData) return;
    const adjustedStep = Math.max(calculateActualStepReached(), 1);
    setStep(adjustedStep);
    setMaxStepReached((prev) => Math.max(prev, adjustedStep));
  }, [isCompletionMode, initialData, calculateActualStepReached]);

  // Update vehicle details when initialData changes - BUT only if user hasn't edited yet
  // CRITICAL: Don't overwrite user changes! Once user starts editing, ignore ALL initialData updates
  // This useEffect handles initial data from URL parameters (car-post-an-ad page) or edit mode
  useEffect(() => {
    // ONLY sync from initialData if user hasn't edited yet
    // This prevents overwriting user changes when they edit make/model/year
    // But allows syncing when initialData comes from URL (async in CarPostAnAd.tsx)
    // IMPORTANT: Use requestAnimationFrame + setTimeout to avoid React warnings about setState during render
    if (initialData?.vehicleDetails && !userEditedVehicleDetailsRef.current) {
      // Check if incoming data has more complete info than current
      const hasIncomingBasicInfo =
        initialData.vehicleDetails.make &&
        initialData.vehicleDetails.model &&
        initialData.vehicleDetails.year;
      const hasCurrentBasicInfo =
        vehicleDetails.make && vehicleDetails.model && vehicleDetails.year;

      // Sync if incoming data is more complete, or if we don't have basic info yet
      if (hasIncomingBasicInfo && !hasCurrentBasicInfo) {
        // Use requestAnimationFrame + setTimeout to ensure this happens after render
        requestAnimationFrame(() => {
          setTimeout(() => {
            // Merge with existing to preserve equipment array if it exists
            setVehicleDetails((prev) => ({
              ...prev,
              ...storedVehicleDetailsToForm(initialData.vehicleDetails),
              equipment:
                initialData.vehicleDetails.equipment || prev.equipment || [],
            }));
            setIsAutoLoaded(true);
            // Update prevBasicVehicleInfoRef when syncing from initialData
            prevBasicVehicleInfoRef.current = {
              make: initialData.vehicleDetails.make,
              model: initialData.vehicleDetails.model,
              year: initialData.vehicleDetails.year,
            };
          }, 0);
        });
      } else if (!hasCurrentBasicInfo && initialData.vehicleDetails.make) {
        // Even if incoming data is incomplete, sync it if we have nothing
        // Use requestAnimationFrame + setTimeout to ensure this happens after render
        requestAnimationFrame(() => {
          setTimeout(() => {
            setVehicleDetails((prev) => ({
              ...prev,
              ...storedVehicleDetailsToForm(initialData.vehicleDetails),
              equipment:
                initialData.vehicleDetails.equipment || prev.equipment || [],
            }));
            setIsAutoLoaded(true);
            prevBasicVehicleInfoRef.current = {
              make: initialData.vehicleDetails.make,
              model: initialData.vehicleDetails.model,
              year: initialData.vehicleDetails.year,
            };
          }, 0);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.vehicleDetails, initialData?.regNumber]); // Watch for initialData changes (e.g., from URL params)

  // Update reg number when initialData changes - check both top-level and vehicleDetails
  useEffect(() => {
    const newRegNumber =
      initialData?.regNumber || initialData?.vehicleDetails?.regNumber;
    if (newRegNumber) {
      setRegNumber(newRegNumber);
    }
  }, [initialData?.regNumber, initialData?.vehicleDetails?.regNumber]);

  // Update createdTestId when initialData.id changes (e.g., when loading an existing test)
  useEffect(() => {
    if (initialData?.id && !createdTestId) {
      setCreatedTestId(initialData.id);
      createdTestIdRef.current = initialData.id; // Also update ref
    }
  }, [initialData?.id, createdTestId]);

  // Sync purchasePrice when initialData changes
  useEffect(() => {
    if (initialData?.purchasePrice !== undefined) {
      setPurchasePrice(initialData.purchasePrice);
    }
  }, [initialData?.purchasePrice]);

  // Baseline after edit-mode init (state is normalized vs raw initialData, e.g. extraTireSet).
  // Image dirtiness is tracked separately via imageStepDirty — do NOT compare `images` here.
  // VehicleImageUploadSection normalizes/pushes images on mount (admin edit starts on step 3),
  // which would otherwise false-trigger unsaved-changes.
  const editBaselineRef = useRef<{
    id?: string;
    nonImage?: string;
    settling?: boolean;
  }>({});

  const captureEditBaseline = useCallback(() => {
    if (!isEditMode || !initialData?.id) return;
    editBaselineRef.current = {
      id: String(initialData.id),
      nonImage: JSON.stringify({
        regNumber,
        vehicleDetails,
        vehicleTestData,
        price,
        comments,
        vatCar,
        status,
      }),
      settling: false,
    };
    setHasNonImageChanges(false);
  }, [
    isEditMode,
    initialData?.id,
    regNumber,
    vehicleDetails,
    vehicleTestData,
    price,
    comments,
    vatCar,
    status,
  ]);

  // Absorb sync init (normalized vehicleDetails, child onChange) before locking baseline.
  useEffect(() => {
    if (!isEditMode || !initialData?.id) return;
    const id = String(initialData.id);
    editBaselineRef.current = { id, nonImage: undefined, settling: true };
    setHasNonImageChanges(false);

    const t = window.setTimeout(() => {
      if (editBaselineRef.current.id === id) {
        editBaselineRef.current.settling = false;
      }
    }, 400);
    return () => clearTimeout(t);
  }, [isEditMode, initialData?.id]);

  useEffect(() => {
    if (!isEditMode || !initialData) return;

    const nonImageSnapshot = JSON.stringify({
      regNumber,
      vehicleDetails,
      vehicleTestData,
      price,
      comments,
      vatCar,
      status,
    });
    const id = String(initialData.id ?? "");

    if (editBaselineRef.current.id !== id) {
      editBaselineRef.current = {
        id,
        nonImage: nonImageSnapshot,
        settling: true,
      };
      setHasNonImageChanges(false);
      return;
    }

    if (
      editBaselineRef.current.settling ||
      editBaselineRef.current.nonImage == null
    ) {
      editBaselineRef.current.nonImage = nonImageSnapshot;
      setHasNonImageChanges(false);
      return;
    }

    const hasNonImageDataChanged =
      nonImageSnapshot !== editBaselineRef.current.nonImage;

    setHasNonImageChanges(hasNonImageDataChanged);
  }, [
    regNumber,
    vehicleDetails,
    vehicleTestData,
    price,
    comments,
    vatCar,
    status,
    isEditMode,
    initialData,
  ]);

  // Simulate reg number search
  const handleRegNumberSubmit = async (
    reg: string,
    isChassisSearch: boolean,
  ) => {
    if (!reg || reg.trim() === "") return;
    setLoading(true);
    setNotFound(false);
    setRateLimited(false);
    setShowSuccess(false);
    setHasActiveListing(false);
    setNotOwner(false);
    setRegNumber(reg);

    try {
      // Fetch vehicle basic information from AutoUncle
      let vehicleInfo;
      try {
        vehicleInfo = await getVehicleBasicInformationByLpn(reg);

        // Update vehicleDetails state with the response data
        if (vehicleInfo?.data?.car) {
          const car = vehicleInfo.data.car;

          // Calculate the updated vehicleDetails BEFORE setting state
          // This ensures we have the complete data available
          let updatedVehicleDetails: Partial<VehicleDetails>;

          // INFO: Poor information retrieval regarding MCs from Autouncle
          // Check if critical fields are missing - but be more lenient
          // Only return early if ALL THREE critical fields are missing
          const hasBrand = car.brand && car.brand !== "";
          const hasModel = car.car_model && car.car_model !== "";
          const hasYear =
            car.year &&
            car.year !== 0 &&
            car.year !== null &&
            car.year !== undefined;

          const missingAllCriticalFields = !hasBrand && !hasModel && !hasYear;

          if (missingAllCriticalFields) {
            // Minimal data - only set what we have
            updatedVehicleDetails = {
              equipment: [],
              registrationDate: (car as any).registrationDate || undefined,
              vin: car.vin || undefined,
              engineVolume: car.engineVolume || undefined,
              regNumber: reg,
            };
          } else {
            // The backend envelope already matches the selector's expected shape.
            const carInfoForSelector = {
              ...car,
              km: car.km || 0,
              variant: car.variant || "",
            };

            // Use convertAutoUncleToVehicleDetails to get engine string (same as valuation)
            const convertedDetails = convertAutoUncleToVehicleDetails(
              carInfoForSelector as any,
            );

            // IMPORTANT: Elbilar är alltid automatiska, så sätt "Automat" direkt för elbilar
            const isElectric =
              convertedDetails.fuelType === "El" ||
              convertedDetails.fuelType === "Elbil" ||
              convertedDetails.fuelType === "ELECTRIC" ||
              car.fuel === "El" ||
              car.fuel === "Elbil" ||
              car.fuel === "ELECTRIC";

            // Shared mapper - see toVehicleDetailsPatch. A hand-written field list here
            // is what silently dropped newly added lookup fields.
            updatedVehicleDetails = {
              ...toVehicleDetailsPatch(carInfoForSelector as any),
              ...(isElectric ? { transmission: "Automat" } : {}),
            };

            // DO NOT populate engine from AutoUncle - user must set it manually
            // Engine field should always be set by user, never from AutoUncle
          }

          // DEBUG: Uncomment for debugging data mapping issues
          // console.log('[CarPostAnAd] 📝 Final updatedVehicleDetails before setState:', {
          //   make: updatedVehicleDetails.make,
          //   model: updatedVehicleDetails.model,
          //   variant: updatedVehicleDetails.variant,
          //   year: updatedVehicleDetails.year,
          //   fuelType: updatedVehicleDetails.fuelType,
          //   bodyType: updatedVehicleDetails.bodyType,
          //   transmission: updatedVehicleDetails.transmission,
          //   color: updatedVehicleDetails.color,
          //   engineVolume: updatedVehicleDetails.engineVolume,
          //   horsepower: updatedVehicleDetails.horsepower,
          //   doors: updatedVehicleDetails.doors,
          //   mileage: updatedVehicleDetails.mileage,
          //   equipment: updatedVehicleDetails.equipment,
          //   vin: updatedVehicleDetails.vin,
          //   regNumber: updatedVehicleDetails.regNumber,
          // });

          // IMPORTANT: Set isAutoLoaded FIRST, then vehicleDetails
          // This ensures useVehicleForm hook can properly detect the auto-load trigger
          setIsAutoLoaded(true);

          // IMPORTANT: Set ALL data at once - merge with existing to preserve any user edits
          setVehicleDetails((prev) => ({
            ...prev,
            ...updatedVehicleDetails,
          }));

          // Store verification data for later (when user clicks "Nästa" in details section)
          // Check if engine volume needs verification for non-electric vehicles
          // Trigger verification if engine volume is missing, invalid, or suspiciously low (< 0.8L)
          const isElectric =
            car.fuel === "El" ||
            car.fuel === "Elbil" ||
            car.fuel === "ELECTRIC";
          const engineVolumeNum = car.engineVolume
            ? parseFloat(car.engineVolume)
            : null;
          // Motorcycles legitimately have tiny engines (0.125L is a real moped engine),
          // so the "suspiciously low" heuristic must not fire for them.
          const isMc = car.vehicleType === "mc";
          const needsEngineVolumeVerification =
            !isElectric &&
            !isMc &&
            (engineVolumeNum === null ||
              isNaN(engineVolumeNum) ||
              engineVolumeNum < 0.8);

          if (needsEngineVolumeVerification) {
            setVerificationData({
              transmission: car.transmission,
              vehicleType: car.vehicleType,
              engineVolume: car.engineVolume,
              fuelType: car.fuel,
              bodyType: car.body,
              doors: car.doors,
              horsePower:
                car.hp !== undefined && car.hp !== null
                  ? car.hp.toString()
                  : undefined,
              electricDriveRange: car.electricDriveRange,
            });
          } else {
            setVerificationData(null);
          }
        }

        if (vehicleInfo?.data?.doManualSearch) {
          setNotFound(true);
          setLoading(false);
          setCheckingActiveListing(false);
          setPendingRegNumber(null);
          return;
        }
      } catch (error: unknown) {
        // Check if it's an active listing error
        if (
          error &&
          typeof error === "object" &&
          "errorCode" in error &&
          error.errorCode === "ACTIVE_LISTING"
        ) {
          setHasActiveListing(true);
          setLoading(false);
          return;
        }

        if (
          error &&
          typeof error === "object" &&
          "errorCode" in error &&
          error.errorCode === "NOT_OWNER"
        ) {
          setNotOwner(true);
          setLoading(false);
          return;
        }

        if (
          error &&
          typeof error === "object" &&
          (("errorCode" in error &&
            error.errorCode === "CARINFO_RATE_LIMITED") ||
            ("status" in error && Number(error.status) === 429))
        ) {
          setRateLimited(true);
          setLoading(false);
          setCheckingActiveListing(false);
          setPendingRegNumber(null);
          return;
        }

        // Handle other errors (404 or 500) - show notFound state
        setNotFound(true);
        setLoading(false);
        setCheckingActiveListing(false);
        setPendingRegNumber(null);
        return;
      }

      setShowSuccess(true);
      setLoading(false);
      setStep(1);
    } catch (error) {
      const apiError = error as {
        status?: number | string;
        errorCode?: string | null;
      };
      if (
        apiError?.errorCode === "CARINFO_RATE_LIMITED" ||
        Number(apiError?.status) === 429
      ) {
        setRateLimited(true);
      } else {
        setNotFound(true);
      }
      setLoading(false);
      setCheckingActiveListing(false);
      setPendingRegNumber(null);
    }
  };

  const handleRetrySearch = () => {
    setStep(0);
    setNotFound(false);
    setRateLimited(false);
    setHasActiveListing(false);
    setNotOwner(false);
    setRegNumber("");
    setIsAutoLoaded(false);
    setCheckingActiveListing(false);
    setPendingRegNumber(null);
  };

  const handleManualEntry = () => {
    // Reset EVERYTHING for manual entry but stay on step 1 (Fordonsinfo)
    setStep(1); // Stay on Fordonsinfo step
    setMaxStepReached(0); // Reset max step reached
    setNotFound(false);
    setRateLimited(false);
    setIsAutoLoaded(false);
    setIsManualMode(true); // Enable manual mode to show reg number input
    // Reset ALL vehicle details explicitly to ensure complete reset
    setVehicleDetails({
      equipment: [],
      make: undefined,
      model: undefined,
      year: undefined,
      fuelType: undefined,
      engine: undefined,
      bodyType: undefined,
      transmission: undefined,
      color: undefined,
      vehicleType: undefined,
      mileage: undefined,
      electricDriveRange: undefined,
      horsepower: undefined,
      vin: undefined,
      regNumber: undefined,
      currentTireType: undefined,
      currentRimType: undefined,
    });
    setRegNumber(""); // Reset registration number
    setVehicleTestData({}); // Reset vehicle test data
    setImages({}); // Reset images
    setPrice(""); // Reset price
    setComments(""); // Reset comments
    setVatCar(false); // Reset VAT car
    // Reset any other states that might affect the form
    userEditedVehicleDetailsRef.current = false;
    // Force a complete re-mount of VehicleDetailsForm by updating key
    setFormResetKey((prev) => prev + 1);
  };

  const handleVehicleDetailsSubmit = (details: Partial<VehicleDetails>) => {
    // Check if basic vehicle info (make, model, year) has changed
    const basicInfoChanged =
      (details.make !== undefined &&
        details.make !== prevBasicVehicleInfoRef.current.make) ||
      (details.model !== undefined &&
        details.model !== prevBasicVehicleInfoRef.current.model) ||
      (details.year !== undefined &&
        details.year !== prevBasicVehicleInfoRef.current.year);

    // Check if engine was cleared (model changed and engine is now empty)
    const modelChanged =
      details.model !== undefined &&
      details.model !== prevBasicVehicleInfoRef.current.model;
    const engineWasCleared =
      modelChanged && (details.engine === "" || details.engine === undefined);

    // Mark that user has edited vehicleDetails to prevent overwriting from initialData
    userEditedVehicleDetailsRef.current = true;

    // Use functional update to ensure we merge with existing data correctly
    setVehicleDetails((prev) => {
      const updated = { ...prev, ...details };
      // If manual mode and regNumber is provided, include it
      if (isManualMode && regNumber) {
        updated.regNumber = regNumber;
      }

      // Update the ref with new values after basic info changes
      if (basicInfoChanged) {
        prevBasicVehicleInfoRef.current = {
          make: updated.make,
          model: updated.model,
          year: updated.year,
        };
      }

      // If basic info changed, reset maxStepReached and go back to step 1
      if (basicInfoChanged) {
        setMaxStepReached(1); // Only allow up to Fordonsinfo step
        setStep((currentStep) => {
          // Go back to Fordonsinfo if on a later step
          if (currentStep > 1) {
            return 1;
          }
          return currentStep;
        });
      }

      // When model changes, useVehicleForm clears engine (sets engine: "")
      // Check if engine was cleared - prev had engine, but updated doesn't
      const wasEngineCleared =
        !!prev.engine &&
        prev.engine !== "" &&
        (!updated.engine || updated.engine === "");

      // If model changed, engine is always cleared in useVehicleForm
      // Check if Bildetaljer is still complete after the change
      if (modelChanged) {
        // Check if Bildetaljer is complete (engine is required for non-electric vehicles)
        const bildetaljerComplete = isBildetaljerComplete(updated);

        // If Bildetaljer is not complete (because engine is missing), cap maxStepReached at 2
        if (!bildetaljerComplete) {
          setMaxStepReached((prevMaxStep) => {
            // Cap at Bildetaljer step (can't go to Fordonstest without engine)
            return Math.min(prevMaxStep, 2);
          });
        }
      }

      // Merge to preserve fields that aren't being updated
      return updated;
    });
  };

  const handleVehicleTestDataSubmit = (data: VehicleTestData) => {
    setVehicleTestData(data);
  };

  // Handle estimation data received from PriceSection
  const handleEstimationDataReceived = useCallback(
    (estimationData: VehicleEstimationResponse) => {
      // Update vehicleDetails with estimation data
      setVehicleDetails((prev) => ({
        ...prev,
        estimatedMarketValue: estimationData.estimatedMarketValue,
        purchasePrice: estimationData.purchasePrice,
      }));
    },
    [],
  );

  const handleSaveChanges = async () => {
    if (!isEditMode || !initialData?.id) {
      return;
    }

    setIsSavingChanges(true);
    try {
      // Build the update request data object
      // NOTE: images are NOT included here - they are handled via lazy upload pattern (separate endpoint)
      const updateRequest: UpdateTestRequest = {
        vehicleName: `${vehicleDetails.make} ${vehicleDetails.model}`,
        vehicleDetails: vehicleDetails as VehicleDetails,
        vehicleTestData: vehicleTestData,
        // images: images, // REMOVED: Images are handled via lazy upload pattern, not in update request
        price: price,
        comments: comments,
        vatCar: vatCar,
        customerId: user?.id?.toString() || initialData.customerId,
      };

      // Update existing test
      const updatedTest = await updateTest({
        testId: initialData.id,
        updateData: updateRequest,
      });

      // Call onTestSaved callback if provided
      if (onTestSaved) {
        onTestSaved(updatedTest);
      }

      // Reset dirty baseline so closing after save does not warn
      captureEditBaseline();
      setImageStepDirty(false);

      // Show success message
      toast.success("Ändringar sparade framgångsrikt!");

      // Navigate to car-test page after successful save
      if (user?.type === UserType.Admin) {
        navigate("/admin/car-test");
      } else {
        navigate("/car-test");
      }
    } catch (error) {
      toast.error("Ett fel uppstod vid sparande av ändringar");
    } finally {
      setIsSavingChanges(false);
    }
  };

  // Handle saving draft (create mode only)
  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      // Clear previous success message
      setSuccessMessage("");

      // If testId exists (either from initialData or createdTestId), we're updating an existing draft - images are already uploaded via lazy upload
      // So we should use updateTest instead of createTest
      const currentTestId = createdTestId || initialData?.id;
      if (currentTestId) {
        // Ensure regNumber is included in vehicleDetails
        const vehicleDetailsWithRegNumber = {
          ...vehicleDetails,
          regNumber: regNumber || vehicleDetails.regNumber || "",
        };

        // Build vehicle name - use existing vehicleName if make/model are missing
        const vehicleName =
          vehicleDetails.make && vehicleDetails.model
            ? `${vehicleDetails.make} ${vehicleDetails.model}`
            : initialData?.regNumber || "Unknown Vehicle";

        // If user is on Test step (step 2) but hasn't answered speed question yet,
        // automatically set speed to 70 km/h when saving draft
        const vehicleTestDataWithDefaultSpeed =
          step >= 2 && !vehicleTestData.speed
            ? { ...vehicleTestData, speed: 70 }
            : vehicleTestData;

        // Update state if speed was set automatically
        if (step >= 2 && !vehicleTestData.speed) {
          setVehicleTestData(vehicleTestDataWithDefaultSpeed);
        }

        const updateRequest: UpdateTestRequest = {
          vehicleName: vehicleName,
          vehicleDetails: vehicleDetailsWithRegNumber as VehicleDetails,
          vehicleTestData: vehicleTestDataWithDefaultSpeed,
          // images: images, // REMOVED: Images are handled via lazy upload pattern, not in update request
          price: price,
          comments: comments,
          vatCar: vatCar,
          customerId: user?.id?.toString() || initialData.customerId,
        };

        const updatedTest = await updateTest({
          testId: currentTestId,
          updateData: updateRequest,
        });

        // Call onTestSaved callback if provided
        if (onTestSaved) {
          onTestSaved(updatedTest);
        }

        toast.success("Utkast uppdaterat framgångsrikt!");

        // Reset dirty baseline so closing after save does not warn
        captureEditBaseline();
        setImageStepDirty(false);

        // Navigate to car-test page after successful save
        if (user?.type === UserType.Admin) {
          navigate("/admin/car-test");
        } else {
          navigate("/car-test");
        }
        return;
      }

      // If test was already created when navigating to Bilder step, use that testId
      // Otherwise create new test as draft
      let testId = createdTestId;

      if (!testId) {
        // Create new test as draft (no testId yet)
        // NOTE: Send service details in imageFiles even for lazy upload - service details are not images
        // Images will be uploaded AFTER test is created via lazy upload

        // If user is on Test step (step 2) but hasn't answered speed question yet,
        // automatically set speed to 70 km/h when saving draft
        const vehicleTestDataWithDefaultSpeed =
          step >= 2 && !vehicleTestData.speed
            ? { ...vehicleTestData, speed: 70 }
            : vehicleTestData;

        // Update state if speed was set automatically
        if (step >= 2 && !vehicleTestData.speed) {
          setVehicleTestData(vehicleTestDataWithDefaultSpeed);
        }

        const createRequest: CreateTestRequest = {
          vehicleName: `${vehicleDetails.make} ${vehicleDetails.model}`,
          vehicleDetails: {
            ...vehicleDetails,
            regNumber: regNumber,
          } as VehicleDetails,
          vehicleTestData: vehicleTestDataWithDefaultSpeed,
          imageFiles: {
            // Include service details even though images are lazy uploaded
            ...(imageFiles.serviceDetails
              ? { serviceDetails: imageFiles.serviceDetails }
              : {}),
          },
          price: price,
          comments: comments,
          vatCar: vatCar,
          customerId: user?.id?.toString() || "",
          isDraft: true,
        };

        const createdTest = await createTest(createRequest);
        testId = createdTest?.id;

        // Store the created testId
        if (testId) {
          setCreatedTestId(testId);
          createdTestIdRef.current = testId; // Also update ref
          // Service details were persisted on create — clear pending so submit won't duplicate
          if (imageFiles.serviceDetails?.serviceDetails?.length) {
            setImageFiles((prev) => {
              const { serviceDetails: _sd, ...rest } = prev;
              return rest;
            });
            imageUploadRef.current?.clearPendingServiceDetails?.();
          }
        }
      }
      if (testId && Object.keys(imageFiles).length > 0) {
        try {
          // Upload all images from imageFiles using hook
          for (const [section, filesOrTires] of Object.entries(imageFiles)) {
            // Handle tires separately since it has nested structure {primary: [], secondary: []}
            if (
              section === "tires" &&
              filesOrTires &&
              typeof filesOrTires === "object" &&
              !Array.isArray(filesOrTires)
            ) {
              const tires = filesOrTires as {
                primary?: { file: File; position?: string }[];
                secondary?: { file: File; position?: string }[];
              };
              // Upload primary tires
              if (tires.primary && Array.isArray(tires.primary)) {
                for (const fileObj of tires.primary) {
                  const file = fileObj.file;
                  if (!file) continue;
                  try {
                    await addCarTestImage({
                      carTestId: testId,
                      category: "tires",
                      file,
                      tireType: "primary",
                    });
                  } catch (error) {
                    toast.error(`Kunde inte ladda upp bild: ${file.name}`);
                  }
                }
              }
              // Upload secondary tires
              if (tires.secondary && Array.isArray(tires.secondary)) {
                for (const fileObj of tires.secondary) {
                  const file = fileObj.file;
                  if (!file) continue;
                  try {
                    await addCarTestImage({
                      carTestId: testId,
                      category: "tires",
                      file,
                      tireType: "secondary",
                    });
                  } catch (error) {
                    toast.error(`Kunde inte ladda upp bild: ${file.name}`);
                  }
                }
              }
              continue;
            }

            // Handle other sections (exterior, interior, damage, serviceImages)
            if (!Array.isArray(filesOrTires) || filesOrTires.length === 0)
              continue;

            const categoryMap: Record<
              string,
              "exterior" | "interior" | "damage" | "service" | "tires"
            > = {
              exterior: "exterior",
              interior: "interior",
              damage: "damage",
              serviceImages: "service",
              tires: "tires",
            };
            const category = categoryMap[section] || "exterior";

            for (const fileObj of filesOrTires) {
              const file = fileObj.file;
              if (!file) continue;

              try {
                // Explicitly convert isThumbnail to boolean for exterior images
                const isThumbnail =
                  category === "exterior" && "isThumbnail" in fileObj
                    ? fileObj.isThumbnail === true
                      ? true
                      : false
                    : undefined;

                await addCarTestImage({
                  carTestId: testId,
                  category,
                  file,
                  isThumbnail,
                  damageComment:
                    category === "damage" && "comment" in fileObj
                      ? fileObj.comment
                      : undefined,
                });
              } catch (error) {
                toast.error(`Kunde inte ladda upp bild: ${file.name}`);
              }
            }
          }
        } catch (error) {
          toast.warning(lp.widget.testCreatedImagesError);
        }
      }

      // Show success toast
      toast.success(ct.messages.toastDraftSavedSuccess);

      // Call onTestSaved to close dialog
      if (onTestSaved) {
        onTestSaved({} as UpdateTestResponse);
      }

      // Force close dialog
      if (onOpenChange) {
        onOpenChange(false);
      }

      // Navigate to car-test page after successful save
      if (user?.type === UserType.Admin) {
        navigate("/admin/car-test");
      } else {
        navigate("/car-test");
      }
    } catch (error) {
      toast.warning(
        "Något gick fel när utkastet skulle sparas. Vänligen försök igen.",
      );
    }
  };

  const handleSubmitForReview = async () => {
    if (user?.type === UserType.Admin) {
      setShowSelectSellerDialog(true);
      return;
    }

    // ID verification not required to submit car tests for review (aligned with backend).

    if (!userDeliverySkipRef.current) {
      try {
        const { data } = await refetchDeliveryAddresses();
        const addresses = data ?? deliveryAddresses;
        if (!addresses?.length) {
          setUserDeliveryPromptOpen(true);
          return;
        }
      } catch {
        if (!deliveryAddresses?.length) {
          setUserDeliveryPromptOpen(true);
          return;
        }
      }
    }

    await doSubmitForReview();
  };

  const doSubmitForReview = async (sellerId?: string) => {
    setReviewSubmitBusy(true);
    try {
      // Clear previous success message
      setSuccessMessage("");

      // Validate exterior *photo* exists (motor video alone is not enough / never thumbnail)
      const { isVideoMedia } = await import("@/lib/isVideoMedia");
      const exteriorImages = images?.exterior || [];
      const hasExteriorPhoto = exteriorImages.some(
        (img) => img.url && !isVideoMedia(img.type, img.url),
      );
      if (!hasExteriorPhoto) {
        toast.error(
          "Du behöver ladda upp minst en exteriörbild innan du kan skicka för granskning.",
        );
        return;
      }

      const sellerUserIdForDelivery =
        sellerId ||
        selectedSeller?._id ||
        user?.id?.toString() ||
        initialData?.customerId;

      if (
        user?.type === UserType.Admin &&
        sellerUserIdForDelivery &&
        !sellerDeliveryGatePassedRef.current
      ) {
        try {
          const sellerAddrs = await fetchUserDeliveryAddressesAsync(
            sellerUserIdForDelivery,
          );
          if (!sellerAddrs?.length) {
            setAdminSellerDeliveryUserId(sellerUserIdForDelivery);
            pendingAdminSubmitSellerIdRef.current = sellerId;
            setAdminSellerDeliveryPromptOpen(true);
            return;
          }
        } catch (e) {
          toast.error(sd.couldNotVerifySellerDelivery);
          return;
        }
      }

      // If testId exists (either from initialData or createdTestId), we're submitting an existing draft - images are already uploaded via eager upload
      // So we should use updateTest and submit instead of createTest
      // NOTE: When testId exists, images are uploaded immediately via eager upload in VehicleImageUploadSection
      // So we don't need to upload them again here
      // CRITICAL: Always check createdTestId first - if test was created in step 3 (Bilder), use it
      // Use ref to get the latest value, even if React state hasn't updated yet
      const currentTestId =
        createdTestIdRef.current || createdTestId || initialData?.id;
      if (currentTestId) {
        const updateRequest: UpdateTestRequest = {
          vehicleName: `${vehicleDetails.make} ${vehicleDetails.model}`,
          vehicleDetails: {
            ...vehicleDetails,
            regNumber: regNumber,
          } as VehicleDetails,
          vehicleTestData: vehicleTestData,
          // images: images, // REMOVED: Images are handled via lazy upload pattern, not in update request
          price: price,
          comments: comments,
          vatCar: vatCar,
          // CRITICAL: Use sellerId parameter if provided (from SelectSellerDialog), otherwise use selectedSeller state, otherwise fall back to current user or initialData
          customerId:
            sellerId ||
            selectedSeller?._id ||
            user?.id?.toString() ||
            initialData.customerId,
        };

        // First update the test
        await updateTest({
          testId: currentTestId,
          updateData: updateRequest,
        });

        // Only POST service details that were added AFTER create (create already persisted any
        // that were present then). Clearing after create prevents WAG49C-style duplicates.
        if (
          imageFiles.serviceDetails?.serviceDetails &&
          imageFiles.serviceDetails.serviceDetails.length > 0
        ) {
          const imageUpdateRequest: UpdateVehicleTestImagesRequest = {
            testId: currentTestId,
            addImages: [],
            removeImages: [],
            addServiceDetails: imageFiles.serviceDetails.serviceDetails,
          };
          await updateVehicleTestImages(imageUpdateRequest);
          setImageFiles((prev) => {
            const { serviceDetails: _sd, ...rest } = prev;
            return rest;
          });
          imageUploadRef.current?.clearPendingServiceDetails?.();
        }

        // Then submit it for review
        await submitTest(currentTestId);

        toast.success(ct.messages.toastSubmittedForReview);
        sellerDeliveryGatePassedRef.current = false;

        // Call onTestSaved callback if provided
        if (onTestSaved) {
          onTestSaved({} as UpdateTestResponse);
        }

        // Force close dialog
        if (onOpenChange) {
          onOpenChange(false);
        }

        // Navigate to /car-test page with "submitted" tab (or /admin/car-test for admin)
        if (!isEditMode) {
          if (user?.type === UserType.Admin) {
            navigate("/admin/car-test");
          } else {
            navigate("/car-test?tab=submitted");
          }
        }
        return;
      }

      // CRITICAL: If we reach here, it means currentTestId was undefined, which should not happen
      // if test was created in step 3 (Bilder). This is a fallback for edge cases.
      // However, we should NOT create a new test if createdTestId exists but wasn't checked properly
      // Double-check createdTestId one more time before creating a new test
      // This should never happen if test was created in step 3, but if it does, log an error
      // Use ref to get the latest value, even if React state hasn't updated yet
      const fallbackTestId =
        createdTestIdRef.current || createdTestId || initialData?.id;
      if (fallbackTestId) {
        const updateRequest: UpdateTestRequest = {
          vehicleName: `${vehicleDetails.make} ${vehicleDetails.model}`,
          vehicleDetails: {
            ...vehicleDetails,
            regNumber: regNumber,
          } as VehicleDetails,
          vehicleTestData: vehicleTestData,
          price: price,
          comments: comments,
          vatCar: vatCar,
          // CRITICAL: Use sellerId parameter if provided (from SelectSellerDialog), otherwise use selectedSeller state, otherwise fall back to current user or initialData
          customerId:
            sellerId ||
            selectedSeller?._id ||
            user?.id?.toString() ||
            initialData?.customerId,
        };

        await updateTest({
          testId: fallbackTestId,
          updateData: updateRequest,
        });

        await submitTest(fallbackTestId);

        toast.success(ct.messages.toastSubmittedForReview);
        sellerDeliveryGatePassedRef.current = false;

        if (onTestSaved) {
          onTestSaved({} as UpdateTestResponse);
        }

        if (onOpenChange) {
          onOpenChange(false);
        }

        if (!isEditMode) {
          if (user?.type === UserType.Admin) {
            navigate("/admin/car-test");
          } else {
            navigate("/car-test?tab=submitted");
          }
        }
        return;
      }

      // Only create new test if we truly don't have one (edge case)
      let testId = undefined;

      if (!testId) {
        // Create new test (no testId yet) - images will be uploaded after test creation
        // NOTE: Send service details in imageFiles even for lazy upload - service details are not images

        // Debug logging to verify service details are included
        // Use imageFiles state - it should contain service details if they were added
        // The VehicleImageUploadSection's useEffect should have updated imageFiles via onImageFilesChange
        const imageFilesToSend: any = {
          // Include service details even though images are lazy uploaded
          ...(imageFiles.serviceDetails
            ? { serviceDetails: imageFiles.serviceDetails }
            : {}),
        };

        const createRequest: CreateTestRequest = {
          vehicleName: `${vehicleDetails.make} ${vehicleDetails.model}`,
          vehicleDetails: {
            ...vehicleDetails,
            regNumber: regNumber,
          } as VehicleDetails,
          vehicleTestData: vehicleTestData,
          imageFiles: imageFilesToSend,
          price: price,
          comments: comments,
          vatCar: vatCar,
          customerId: sellerId || user?.id?.toString() || "",
          isDraft: false,
        };

        const createdTest = await createTest(createRequest);
        testId = createdTest?.id;

        // Store the created testId
        if (testId) {
          setCreatedTestId(testId);
          createdTestIdRef.current = testId; // Also update ref
          if (imageFiles.serviceDetails?.serviceDetails?.length) {
            setImageFiles((prev) => {
              const { serviceDetails: _sd, ...rest } = prev;
              return rest;
            });
            imageUploadRef.current?.clearPendingServiceDetails?.();
          }
        }
      }

      // NOTE: Images are already uploaded via eager upload when user adds them in Bilder step
      // Since test is created with DRAFT status when navigating to Bilder step, all images
      // are uploaded immediately via eager upload pattern. No need to upload them here.

      // Submit the test
      if (testId) {
        await submitTest(testId);
      }

      // Show success toast instead of success message
      toast.success(ct.messages.toastSubmittedForReview);
      sellerDeliveryGatePassedRef.current = false;

      // Call onTestSaved to close dialog
      if (onTestSaved) {
        onTestSaved({} as UpdateTestResponse); // Empty response for create
      }

      // Force close dialog
      if (onOpenChange) {
        onOpenChange(false);
      }

      // Navigate to /car-test page with "submitted" tab (or /admin/car-test for admin)
      if (!isEditMode) {
        if (user?.type === UserType.Admin) {
          navigate("/admin/car-test");
        } else {
          navigate("/car-test?tab=submitted");
        }
      }
    } catch (error) {
      sellerDeliveryGatePassedRef.current = false;
      // Error is already handled by the useTest hook (toast notification)
      toast.warning(
        "Något gick fel när testet skulle skickas för granskning. Vänligen försök igen.",
      );
    } finally {
      setReviewSubmitBusy(false);
      userDeliverySkipRef.current = false;
    }
  };

  // Step completion logic - shared with the valuation flow, see isVehicleInfoComplete.
  // This flow collects condition in its own later test step, not in step 1.
  const vehicleInfoComplete = isVehicleInfoComplete(vehicleDetails);

  const vehicleTestDataComplete = !!(
    vehicleTestData.speed &&
    vehicleTestData.vibrations?.problem &&
    vehicleTestData.leaks?.problem &&
    vehicleTestData.noises?.problem &&
    vehicleTestData.gearbox?.problem &&
    vehicleTestData.crash?.problem &&
    vehicleTestData.ac?.problem &&
    vehicleTestData.dashboard?.problem &&
    vehicleTestData.windshield?.problem &&
    vehicleTestData.brakes?.frontEffectiveness &&
    vehicleTestData.brakes?.rearEffectiveness &&
    vehicleTestData.tires?.primary?.frontDepth &&
    vehicleTestData.tires?.primary?.rearDepth
  );

  const isStepCompleted = (idx: number) => {
    if (idx === 0) return !!regNumber;
    if (idx === 1) return vehicleInfoComplete;
    if (idx === 2) return vehicleTestDataComplete;
    if (idx === 3) return Object.keys(images).length > 0;
    if (idx === 4) return !!price;
    return false;
  };

  const goToStep = async (newStep: number) => {
    // Validate before allowing step 2 (Fordonstest) - requires engine for non-electric vehicles
    if (newStep === 2) {
      // Check if engine volume verification is needed before advancing
      if (verificationData) {
        const engineVolumeNum = verificationData.engineVolume
          ? parseFloat(verificationData.engineVolume)
          : NaN;
        const isElectric =
          verificationData.fuelType === "El" ||
          verificationData.fuelType === "Elbil" ||
          verificationData.fuelType === "ELECTRIC";

        const needsEngineVolumeVerification =
          !isElectric &&
          verificationData.vehicleType !== "mc" &&
          (isNaN(engineVolumeNum) || engineVolumeNum < 0.8);

        if (needsEngineVolumeVerification) {
          setShowVerificationDialog(true);
          return; // Don't advance yet, wait for user to verify
        }
      }

      const bildetaljerComplete = isBildetaljerComplete(vehicleDetails);

      // Don't allow going to Fordonstest if Bildetaljer is not complete
      if (!bildetaljerComplete) {
        return; // Silently prevent navigation
      }
    }

    // When going to step 3 (Bilder), ensure test exists with DRAFT status
    // This enables eager upload of images
    // Use ref to check if test already exists (handles async state updates)
    if (
      newStep === 3 &&
      !isEditMode &&
      !createdTestIdRef.current &&
      !createdTestId
    ) {
      setIsCreatingTestForImages(true);
      try {
        // Build vehicle name
        const vehicleName =
          vehicleDetails.make && vehicleDetails.model
            ? `${vehicleDetails.make} ${vehicleDetails.model}`
            : regNumber || "Unknown Vehicle";

        // If user hasn't answered speed question yet, set default speed
        const vehicleTestDataWithDefaultSpeed = !vehicleTestData.speed
          ? { ...vehicleTestData, speed: 70 }
          : vehicleTestData;

        // Update state if speed was set automatically
        if (!vehicleTestData.speed) {
          setVehicleTestData(vehicleTestDataWithDefaultSpeed);
        }

        const createRequest: CreateTestRequest = {
          vehicleName: vehicleName,
          vehicleDetails: {
            ...vehicleDetails,
            regNumber: regNumber || vehicleDetails.regNumber || "",
          } as VehicleDetails,
          vehicleTestData: vehicleTestDataWithDefaultSpeed,
          imageFiles: {}, // No images or service details when creating DRAFT test
          price: price || "",
          comments: comments || "",
          vatCar: vatCar,
          customerId: user?.id?.toString() || "",
          isDraft: true,
        };

        const createdTest = await createTest(createRequest);
        const testId = createdTest?.id;

        if (!testId) {
          toast.error("Kunde inte skapa fordonstest. Försök igen.");
          setIsCreatingTestForImages(false);
          return; // Don't advance if test creation failed
        }

        // Set createdTestId immediately (both state and ref to ensure it's always available)
        // This prevents creating duplicate tests when submitting for review
        setCreatedTestId(testId);
        createdTestIdRef.current = testId; // Update ref immediately for synchronous access
        // Also set it in a way that ensures doSubmitForReview can see it immediately
        // by storing it in a way that persists across the async operation
      } catch (error) {
        toast.error("Kunde inte skapa fordonstest. Försök igen.");
        setIsCreatingTestForImages(false);
        return; // Don't advance if test creation failed
      } finally {
        setIsCreatingTestForImages(false);
      }
    }

    // If test already exists and user is going to step 3, update test with current data
    // This ensures test data is up to date if user went back and changed something
    if (newStep === 3 && !isEditMode && createdTestId && step !== 3) {
      // User is navigating to step 3 and test already exists
      // Update test with current data to ensure it's in sync
      try {
        const vehicleName =
          vehicleDetails.make && vehicleDetails.model
            ? `${vehicleDetails.make} ${vehicleDetails.model}`
            : regNumber || "Unknown Vehicle";

        const updateRequest: UpdateTestRequest = {
          vehicleName: vehicleName,
          vehicleDetails: {
            ...vehicleDetails,
            regNumber: regNumber || vehicleDetails.regNumber || "",
          } as VehicleDetails,
          vehicleTestData: vehicleTestData,
          price: price || "",
          comments: comments || "",
          vatCar: vatCar,
          customerId: user?.id?.toString() || "",
        };

        // Update test silently in background (don't show loading or errors to user)
        updateTest({
          testId: createdTestId,
          updateData: updateRequest,
        }).catch((error) => {});
      } catch (error) {}
    }

    setStep(newStep);
    setMaxStepReached((prev) => Math.max(prev, newStep));
  };

  return (
    <Card className="relative w-full max-w-2xl mx-auto px-3 pb-12 sm:px-6 glass-card overflow-x-hidden">
      <CardHeader className="items-center">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full flex-wrap mt-3 sm:mt-6 md:mt-0">
          <CardTitle className="text-lg sm:text-xl md:text-2xl text-center sm:text-left">
            {title || (isEditMode ? ct.actions.editAd : ct.actions.create)}
          </CardTitle>
          {(step > 0 || isEditMode || isCompletionMode) &&
            (regNumber?.trim() || vehicleDetails.regNumber?.trim()) && (
              <LicensePlate
                className="shrink-0"
                lpn={(
                  regNumber ||
                  vehicleDetails.regNumber ||
                  ""
                ).toUpperCase()}
              />
            )}
        </div>
      </CardHeader>

      <div className="flex justify-center gap-1 sm:gap-2 mb-4 sm:mb-6 w-full flex-wrap">
        {(isMobile ? STEP_LABELS_MOBILE : STEP_LABELS)
          .map((label, idx) => ({ label, idx }))
          .filter(({ idx }) => {
            // Hide Reg nr step (step 0) when editing or completing a draft
            if (idx === 0 && (isEditMode || isCompletionMode)) {
              return false;
            }
            return true;
          })
          .map(({ label, idx }) => {
            const isCompleted = isStepCompleted(idx);
            const isClickable = idx <= maxStepReached;
            return (
              <button
                key={label}
                type="button"
                className={`px-4 sm:px-5 py-1.5 rounded-md text-sm sm:text-sm font-medium transition whitespace-nowrap min-h-0 h-9 sm:h-8 shrink-0
                ${
                  step === idx
                    ? "bg-[#D4AF37] text-white shadow"
                    : isCompleted
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 hover:bg-[#D4AF37]/20"
                }
                ${!isClickable ? "opacity-50 cursor-not-allowed" : ""}
              `}
                onClick={async () => {
                  if (isClickable) {
                    await goToStep(idx);
                  }
                }}
                disabled={!isClickable}
              >
                {label}
              </button>
            );
          })}
      </div>

      {step === 1 && isAutoLoaded && (
        <Alert className="mb-4 sm:mb-6 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
          <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            <span className="font-medium">Viktigt:</span> Automatisk
            datahämtning kan ibland innehålla felaktig information. Vänligen
            verifiera att alla uppgifter är korrekta innan du fortsätter.
          </AlertDescription>
        </Alert>
      )}

      {step === 0 &&
        !loading &&
        !notFound &&
        !rateLimited &&
        !showSuccess &&
        !hasActiveListing &&
        !notOwner && <RegistrationInput onSubmit={handleRegNumberSubmit} />}
      {step === 0 && loading && (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-4 border-[#D4AF37] border-solid mb-3 sm:mb-4"></div>
          <span className="text-sm text-muted-foreground">
            {lp.widget?.loadingVehicleData ?? "Hämtar fordonsdata..."}
          </span>
        </div>
      )}
      {step === 0 && showSuccess && (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12">
          <div className="rounded-full h-10 w-10 sm:h-12 sm:w-12 bg-green-500 flex items-center justify-center mb-3 sm:mb-4">
            <svg
              className="h-5 w-5 sm:h-6 sm:w-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <span className="text-sm text-green-600 font-medium">
            Fordonsdata hämtad!
          </span>
        </div>
      )}
      {step === 0 && rateLimited && (
        <div className="flex flex-col items-center justify-center py-6 sm:py-8 space-y-3 sm:space-y-4">
          <div className="text-center space-y-2">
            <p className="text-red-600 font-medium">
              {lp.widget.rateLimitedTitle}
            </p>
            <p className="text-sm text-muted-foreground px-4">
              {lp.widget.rateLimitedHint}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRetrySearch}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.tryAgain}
            </Button>
            <Button
              onClick={handleManualEntry}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.manualEntry}
            </Button>
          </div>
        </div>
      )}
      {step === 0 && notFound && !rateLimited && (
        <div className="flex flex-col items-center justify-center py-6 sm:py-8 space-y-3 sm:space-y-4">
          <div className="text-center space-y-2">
            <p className="text-red-600 font-medium">
              {lp.widget.vehicleNotFound}
            </p>
            <p className="text-sm text-muted-foreground px-4">
              {lp.widget.vehicleNotFoundHint}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRetrySearch}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.tryAgain}
            </Button>
            <Button
              onClick={handleManualEntry}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.manualEntry}
            </Button>
          </div>
        </div>
      )}
      {step === 0 && hasActiveListing && (
        <div className="flex flex-col items-center justify-center py-6 sm:py-8 space-y-3 sm:space-y-4">
          <div className="text-center space-y-2">
            <p className="text-orange-600 font-medium">
              {lp.widget.activeListingTitle}
            </p>
            <p className="text-sm text-muted-foreground px-4">
              {lp.widget.activeListingHint}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRetrySearch}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.tryOtherReg}
            </Button>
            <Button
              onClick={handleManualEntry}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.manualEntry}
            </Button>
          </div>
        </div>
      )}
      {step === 0 && notOwner && (
        <div className="flex flex-col items-center justify-center py-6 sm:py-8 space-y-3 sm:space-y-4">
          <div className="text-center space-y-2">
            <p className="text-red-600 font-medium">
              {lp.widget.notOwnerTitle}
            </p>
            <p className="text-sm text-muted-foreground px-4">
              {lp.widget.notOwnerHint}
            </p>
            <p className="text-sm text-muted-foreground px-4">
              {lp.widget.notOwnerContact}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRetrySearch}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.tryOtherReg}
            </Button>
            <Button
              onClick={handleManualEntry}
              className="px-4 w-full sm:w-auto"
            >
              {lp.widget.manualEntry}
            </Button>
          </div>
        </div>
      )}
      {step === 1 && (
        <>
          <VehicleDetailsForm
            key={formResetKey}
            initialData={vehicleDetails}
            onChange={handleVehicleDetailsSubmit}
            onNext={async () => {
              await goToStep(2);
            }}
            isAutoLoaded={isAutoLoaded}
            showCondition={false}
            isManualMode={isManualMode}
            regNumber={regNumber}
            onRegNumberChange={(newRegNumber) => {
              setRegNumber(newRegNumber);
              // Update vehicleDetails with regNumber when in manual mode
              if (isManualMode) {
                setVehicleDetails((prev) => ({
                  ...prev,
                  regNumber: newRegNumber,
                }));
              }
            }}
          />
        </>
      )}
      {step === 2 && (
        <PostAnAdDetailsForm
          initialData={vehicleTestData}
          onChange={handleVehicleTestDataSubmit}
          onNext={async () => {
            await goToStep(3);
          }}
          isEditMode={isEditMode}
          isCreatingTest={isCreatingTestForImages}
          vehicleDetails={
            vehicleDetails
              ? { extraTireSet: (vehicleDetails as any).extraTireSet }
              : undefined
          }
          onVehicleDetailsChange={(details) => {
            handleVehicleDetailsSubmit({
              extraTireSet: details.extraTireSet,
            } as Partial<VehicleDetails>);
          }}
        />
      )}
      {step === 3 && (
        <>
          {isCreatingTestForImages ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-4 border-[#D4AF37] border-solid mb-3 sm:mb-4"></div>
              <span className="text-sm text-muted-foreground">
                {lp.widget.creatingTest}
              </span>
            </div>
          ) : (
            <VehicleImageUploadSection
              ref={imageUploadRef}
              onNext={async () => {
                await goToStep(4);
              }}
              onChange={handleImagesChange}
              onImageFilesChange={handleImageFilesChange}
              initialData={images}
              isEditMode={isEditMode}
              onSaveImageChanges={handleSaveImageChanges}
              onAfterImageSaveComplete={invalidateTestImages}
              testId={createdTestId || initialData?.id}
              hideIntegratedImageSaveButton={true}
              onImageDirtyChange={setImageStepDirty}
            />
          )}
        </>
      )}
      {step === 4 && (
        <PriceSection
          price={price}
          comments={comments}
          vatCar={vatCar}
          isEditMode={isEditMode}
          isCompletionMode={isCompletionMode}
          testStatus={status}
          // Built by spread: a hand-written literal here used to drop trim,
          // isNotImported, origin and equipment from the estimation payload.
          vehicleInfo={toPriceSectionVehicleInfo(vehicleDetails, {
            regNumber,
            purchasePrice: purchasePrice ?? vehicleDetails?.purchasePrice,
          })}
          onPriceChange={setPrice}
          onCommentsChange={setComments}
          onVatCarChange={setVatCar}
          onSubmitForReview={handleSubmitForReview}
          onEstimationDataReceived={handleEstimationDataReceived}
          isLoading={
            isCreatingTest ||
            isUpdatingTest ||
            isSubmittingTest ||
            isUploadingImages ||
            reviewSubmitBusy
          }
          error={
            createTestError || updateTestError || submitTestError
              ? sanitizeErrorMessage(
                  createTestError ?? updateTestError ?? submitTestError,
                )
              : undefined
          }
          successMessage={successMessage}
          userType={user?.type}
        />
      )}

      <Dialog
        open={userDeliveryPromptOpen}
        onOpenChange={setUserDeliveryPromptOpen}
      >
        <DialogContent
          {...userDeliveryPromptSheetProps}
          className={cn(userDeliveryPromptSheetProps.className, "sm:max-w-md")}
        >
          <DialogHeader>
            <DialogTitle>{sd.userFormTitle}</DialogTitle>
            <DialogDescription className="text-left pt-1">
              {sd.userMissingBody}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setUserDeliveryPromptOpen(false);
                userDeliverySkipRef.current = true;
                void doSubmitForReview();
              }}
            >
              {sd.userAddLater}
            </Button>
            <Button
              onClick={() => {
                setUserDeliveryPromptOpen(false);
                setUserDeliveryFormOpen(true);
              }}
            >
              {sd.userAddYes}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={userDeliveryFormOpen}
        onOpenChange={(open) => {
          setUserDeliveryFormOpen(open);
          if (!open) void refetchDeliveryAddresses();
        }}
      >
        <DialogContent
          {...userDeliveryFormSheetProps}
          className={cn(
            userDeliveryFormSheetProps.className,
            "max-h-[90vh] overflow-y-auto sm:max-w-lg",
          )}
        >
          <DialogHeader>
            <DialogTitle>{sd.userFormTitle}</DialogTitle>
          </DialogHeader>
          <DeliverySection />
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setUserDeliveryFormOpen(false)}
            >
              {lp.widget.cancel}
            </Button>
            <Button
              onClick={async () => {
                const { data } = await refetchDeliveryAddresses();
                const list = data ?? deliveryAddresses;
                if (list?.length) {
                  setUserDeliveryFormOpen(false);
                  await doSubmitForReview();
                } else {
                  toast.error(sd.stillNoAddress);
                }
              }}
            >
              {sd.userFormDone}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={adminSellerDeliveryPromptOpen}
        onOpenChange={setAdminSellerDeliveryPromptOpen}
      >
        <DialogContent
          {...adminSellerDeliveryPromptSheetProps}
          className={cn(
            adminSellerDeliveryPromptSheetProps.className,
            "sm:max-w-lg",
          )}
        >
          <DialogHeader>
            <DialogTitle>{sd.adminFormTitle}</DialogTitle>
            <DialogDescription className="text-left pt-1">
              {sd.adminSellerMissingBody}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setAdminSellerDeliveryPromptOpen(false)}
              >
                {lp.widget.cancel}
              </Button>
              <Button
                variant="secondary"
                disabled={isSendingSellerReminder}
                onClick={async () => {
                  if (!adminSellerDeliveryUserId) return;
                  setIsSendingSellerReminder(true);
                  try {
                    await UserManagementService.sendDeliveryAddressReminderEmail(
                      adminSellerDeliveryUserId,
                    );
                    toast.success(sd.reminderSent);
                  } catch {
                    toast.error(sd.reminderFailed);
                  } finally {
                    setIsSendingSellerReminder(false);
                  }
                }}
              >
                {isSendingSellerReminder ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {lp.widget.saving}
                  </>
                ) : (
                  sd.adminRemindSeller
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setAdminSellerDeliveryPromptOpen(false);
                  setAdminSellerDeliveryFormOpen(true);
                }}
              >
                {sd.adminAddForSeller}
              </Button>
              <Button
                onClick={async () => {
                  setAdminSellerDeliveryPromptOpen(false);
                  sellerDeliveryGatePassedRef.current = true;
                  await doSubmitForReview(
                    pendingAdminSubmitSellerIdRef.current,
                  );
                }}
              >
                {sd.adminContinueAnyway}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={adminSellerDeliveryFormOpen}
        onOpenChange={setAdminSellerDeliveryFormOpen}
      >
        <DialogContent
          {...adminSellerDeliveryFormSheetProps}
          className={cn(
            adminSellerDeliveryFormSheetProps.className,
            "max-h-[90vh] overflow-y-auto sm:max-w-lg",
          )}
        >
          <DialogHeader>
            <DialogTitle>{sd.adminFormTitle}</DialogTitle>
          </DialogHeader>
          {adminSellerDeliveryUserId ? (
            <AdminUserDeliverySection
              userId={adminSellerDeliveryUserId}
              deliveryAddresses={adminSellerDeliveryAddresses}
              isLoading={loadingAdminSellerAddresses}
            />
          ) : null}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setAdminSellerDeliveryFormOpen(false)}
            >
              {lp.widget.cancel}
            </Button>
            <Button
              onClick={async () => {
                const r = await refetchAdminSellerDeliveryAddresses();
                const list = r.data ?? adminSellerDeliveryAddresses;
                if (list?.length) {
                  setAdminSellerDeliveryFormOpen(false);
                  await doSubmitForReview(
                    pendingAdminSubmitSellerIdRef.current,
                  );
                } else {
                  toast.error(sd.stillNoAddress);
                }
              }}
            >
              {sd.adminFormDone}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Select Seller Dialog - Only shown for admin users */}
      <SelectSellerDialog
        open={showSelectSellerDialog}
        onOpenChange={setShowSelectSellerDialog}
        onSelectSeller={(seller) => {
          setSelectedSeller(seller);
          setShowSelectSellerDialog(false);
          // Proceed with submission using selected seller
          doSubmitForReview(seller._id);
        }}
      />

      {/* AutoUncle Verification Dialog */}
      {verificationData && (
      <AutoUncleVerificationDialog
          open={showVerificationDialog}
          onClose={() => {
            setShowVerificationDialog(false);
            setVerificationData(null);
          }}
          onSave={async (engineVolume) => {
            // Update vehicleDetails with verified engineVolume
            setVehicleDetails((prev) => ({
              ...prev,
              engineVolume:
                engineVolume !== undefined ? engineVolume : prev.engineVolume,
            }));
            setShowVerificationDialog(false);
            setVerificationData(null);

            // If we were trying to go to step 2, advance now after verification
            if (step === 1) {
              // DEBUG: Uncomment for debugging verification flow
              // console.log('[CarPostAnAd] ✅ Verification complete, advancing to step 2');
              await goToStep(2);
            }
          }}
          initialEngineVolume={
            vehicleDetails.engineVolume || verificationData.engineVolume
          }
          showEngineVolume={
            verificationData.fuelType !== "El" &&
            verificationData.fuelType !== "Elbil" &&
            verificationData.fuelType !== "ELECTRIC" &&
            verificationData.vehicleType !== "mc" &&
            (!verificationData.engineVolume ||
              verificationData.engineVolume === "" ||
              parseFloat(verificationData.engineVolume) < 0.8)
          }
          vehicleDetails={vehicleDetails}
        />
      )}

      {/* Save Draft / Save Changes Button - Bottom Left */}
      {/* Show "Spara ändringar" when in edit mode and on any step >= 1 */}
      {/* Show "Spara utkast" only when NOT in edit mode and on step >= 2 (Test or later) */}
      {/* Hide "Spara ändringar" button when status is COMPLEMENT */}
      {((isEditMode && maxStepReached >= 1) || (!isEditMode && step >= 2)) &&
        (!isEditMode || initialData?.status !== "complement") && (
          <div className="absolute bottom-2 left-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                if (!isEditMode) {
                  await handleSaveDraft();
                  return;
                }

                setIsSavingChanges(true);
                try {
                  if (step === 3) {
                    await imageUploadRef.current?.savePendingImages?.();
                    setImageStepDirty(false);
                  }
                  if (hasNonImageChanges) {
                    await handleSaveChanges();
                  } else {
                    // Image-only save: clear dirty so close does not warn
                    captureEditBaseline();
                  }
                } finally {
                  setIsSavingChanges(false);
                }
              }}
              className="text-xs bg-blue-600 hover:bg-blue-500 text-white border"
              disabled={
                (isEditMode &&
                  !hasNonImageChanges &&
                  !(step === 3 && imageStepDirty)) ||
                isSavingDraft ||
                isSavingChanges
              }
            >
              {isSavingDraft || isSavingChanges ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  {lp.widget.saving}
                </>
              ) : isEditMode || createdTestId ? (
                lp.widget.saveChanges
              ) : (
                lp.widget.saveDraft
              )}
            </Button>
          </div>
        )}
    </Card>
  );
});
